import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const EQUIPMENT_NAMES = {
  chainsaw: 'Chainsaw',
  wood_chipper: 'Wood Chipper',
  stump_grinder: 'Stump Grinder',
  bucket_truck: 'Bucket Truck',
  crane: 'Crane',
  skid_steer: 'Skid Steer',
  dump_truck: 'Dump Truck',
  grapple_truck: 'Grapple Truck',
  trailer: 'Trailer',
  front_loader: 'Front Loader',
  excavator: 'Excavator',
  bulldozer: 'Bulldozer',
};

const EXPERIENCE_LABELS = {
  '0': 'Less than 1 year',
  '1': '1-2 years',
  '3': '3-5 years',
  '6': '6-10 years',
  '10': '10+ years',
};

function ReviewStep({ data, termsAccepted, onTermsChange }) {
  const [showTermsModal, setShowTermsModal] = useState(false);

  const selectedEquipment = Object.entries(data.equipment_experience || {}).filter(
    ([, value]) => value.hasExperience
  );

  return (
    <div className="review-step">
      <h5 className="mb-4">Review Your Information</h5>
      <p className="text-muted mb-4">
        Please review your information before submitting. Use the Previous button to make changes.
      </p>

      <div className="review-section">
        <h6>Contact Information</h6>
        <div className="review-item">
          <span className="review-label">Name:</span>
          <span className="review-value">{data.first_name} {data.last_name}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Email:</span>
          <span className="review-value">{data.email}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Phone:</span>
          <span className="review-value">{data.phone}</span>
        </div>
        {data.company_name && (
          <div className="review-item">
            <span className="review-label">Company:</span>
            <span className="review-value">{data.company_name}</span>
          </div>
        )}
        <div className="review-item">
          <span className="review-label">Address:</span>
          <span className="review-value">
            {data.address}, {data.city}, {data.state} {data.zip_code}
          </span>
        </div>
      </div>

      <div className="review-section">
        <h6>Work Locations ({data.work_locations.length} states)</h6>
        <div className="d-flex flex-wrap gap-1">
          {data.work_locations.sort().map((code) => (
            <Badge key={code} bg="primary" className="fw-normal">
              {STATE_NAMES[code] || code}
            </Badge>
          ))}
        </div>
      </div>

      <div className="review-section">
        <h6>Equipment Experience</h6>
        {selectedEquipment.length === 0 ? (
          <p className="text-muted">No equipment selected</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Equipment</th>
                  <th>Experience</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {selectedEquipment.map(([id, exp]) => (
                  <tr key={id}>
                    <td>{EQUIPMENT_NAMES[id] || id}</td>
                    <td>{EXPERIENCE_LABELS[exp.years] || exp.years}</td>
                    <td className="text-muted">{exp.details || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="review-section">
        <h6>Additional Information</h6>
        <div className="review-item">
          <span className="review-label">Years in Business:</span>
          <span className="review-value">{data.years_in_business || 0}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Has Insurance:</span>
          <span className="review-value">
            {data.has_insurance ? (
              <Badge bg="success">Yes</Badge>
            ) : (
              <Badge bg="secondary">No</Badge>
            )}
          </span>
        </div>
        {data.has_insurance && data.insurance_details && (
          <div className="review-item">
            <span className="review-label">Insurance Details:</span>
            <span className="review-value">{data.insurance_details}</span>
          </div>
        )}
        {data.additional_notes && (
          <div className="review-item">
            <span className="review-label">Notes:</span>
            <span className="review-value">{data.additional_notes}</span>
          </div>
        )}
      </div>

      <div className="terms-section mt-4 pt-4 border-top">
        <Form.Check
          type="checkbox"
          id="terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => onTermsChange(e.target.checked)}
          label={
            <span>
              I have read and agree to the{' '}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsModal(true);
                }}
              >
                Terms of Use and Privacy Policy
              </button>
            </span>
          }
        />
      </div>

      <Modal show={showTermsModal} onHide={() => setShowTermsModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Terms of Use and Privacy Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Terms of Use</h5>
          <p>
            By submitting this registration form, you agree to the following terms and conditions:
          </p>
          <ol>
            <li className="mb-2">
              <strong>Accuracy of Information:</strong> You certify that all information provided in this registration is accurate and complete to the best of your knowledge.
            </li>
            <li className="mb-2">
              <strong>Eligibility:</strong> You confirm that you are legally authorized to provide tree care and related services in the states you have selected.
            </li>
            <li className="mb-2">
              <strong>Insurance and Licensing:</strong> You agree to maintain all required insurance coverage and licenses for the services you provide.
            </li>
            <li className="mb-2">
              <strong>Communication:</strong> You consent to receive communications from United Arborist regarding potential work opportunities, updates, and other relevant information.
            </li>
            <li className="mb-2">
              <strong>Service Standards:</strong> You agree to uphold professional standards and best practices when performing work coordinated through United Arborist.
            </li>
          </ol>

          <h5 className="mt-4">Privacy Policy</h5>
          <p>
            United Arborist is committed to protecting your privacy. Here's how we handle your data:
          </p>
          <ol>
            <li className="mb-2">
              <strong>Data Collection:</strong> We collect the information you provide in this form, including contact details, work locations, and equipment experience.
            </li>
            <li className="mb-2">
              <strong>Data Use:</strong> Your information is used to match you with appropriate work opportunities and to communicate with you about our services.
            </li>
            <li className="mb-2">
              <strong>Data Sharing:</strong> We may share relevant portions of your information with companies seeking tree services in your service areas.
            </li>
            <li className="mb-2">
              <strong>Data Security:</strong> We implement appropriate security measures to protect your personal information from unauthorized access or disclosure.
            </li>
            <li className="mb-2">
              <strong>Data Retention:</strong> We retain your information for as long as your registration is active or as required by law.
            </li>
            <li className="mb-2">
              <strong>Your Rights:</strong> You may request to view, update, or delete your information at any time by contacting us.
            </li>
          </ol>

          <p className="mt-4 text-muted small">
            Last updated: January 2026
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTermsModal(false)}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => {
              onTermsChange(true);
              setShowTermsModal(false);
            }}
          >
            I Accept
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReviewStep;
